import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import ApiService from '../services/api.service';
import { isPlatformAdmin } from '../utils/roleUtils';

const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const StorageTracking = ({ currentUser }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const canRecompute = isPlatformAdmin(currentUser?.role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [computedAt, setComputedAt] = useState(null);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ApiService.getStorageUsage({ search: search || undefined, page, size: rowsPerPage });
      const pageData = res?.data?.data || {};
      const content = pageData.content || [];
      setRows(content);
      setTotalElements(pageData.total_elements ?? content.length);
      // Show the freshest snapshot time across the page.
      const latest = content.reduce((acc, r) => (r.computed_at && (!acc || r.computed_at > acc) ? r.computed_at : acc), null);
      setComputedAt(latest);
    } catch (err) {
      console.error('Error fetching storage usage:', err);
      setError('Failed to load storage usage');
      setRows([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [search, page, rowsPerPage]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput.trim());
  };

  const handleRecompute = async () => {
    setRecomputing(true);
    setError(null);
    try {
      await ApiService.recomputeStorageUsage();
      await fetchUsage();
    } catch (err) {
      console.error('Error recomputing storage:', err);
      setError(err.response?.data?.error || 'Failed to recompute storage');
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorageIcon sx={{ color: 'secondary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main' }}>
            Storage Tracking
          </Typography>
          <Chip
            label={`${totalElements} Typing Centers`}
            size="small"
            sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.7rem', height: 24 }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {computedAt && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              As of {formatDate(computedAt)}
            </Typography>
          )}
          {canRecompute && (
            <Button
              size="small"
              variant="outlined"
              startIcon={recomputing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRecompute}
              disabled={recomputing}
              sx={{ textTransform: 'none' }}
            >
              {recomputing ? 'Recalculating…' : 'Recalculate'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by typing center name…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          sx={{
            flex: 1,
            minWidth: 220,
            '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', borderRadius: '8px', height: 36 },
            '& .MuiInputBase-input': { fontSize: '0.85rem' }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" size="small" onClick={handleSearch} sx={{ textTransform: 'none' }}>
          Search
        </Button>
        {search && (
          <Tooltip title="Clear search">
            <IconButton size="small" onClick={() => { setSearchInput(''); setSearch(''); setPage(0); }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {error && (
        <Typography variant="body2" sx={{ color: 'error.main', mb: 1.5 }}>{error}</Typography>
      )}

      <Paper sx={{ borderRadius: '10px', overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: isDark ? 'rgba(60,174,163,0.12)' : '#f0f7f7', color: 'text.primary', fontWeight: 700, fontSize: '0.8rem' } }}>
                <TableCell>Typing Center</TableCell>
                <TableCell align="right">Managers Storage</TableCell>
                <TableCell align="right">Mapped Orgs (Thaklis)</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={24} sx={{ color: 'secondary.main' }} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No storage data yet. It refreshes nightly{canRecompute ? ' — or click Recalculate.' : '.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.typing_center_id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{row.typing_center_name || '—'}</TableCell>
                    <TableCell align="right">{formatBytes(row.managers_bytes)}</TableCell>
                    <TableCell align="right">{formatBytes(row.thaklis_bytes)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                      {formatBytes(row.total_bytes)}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {formatDate(row.computed_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default StorageTracking;
